import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject, Injectable } from '@angular/core';
import * as AuthActions from '@core/states/auth/auth.actions';
import {
  catchError,
  exhaustMap,
  map,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@core/states/app.state';
import * as AuthSelectors from '@core/states/auth/auth.selectors';
import { AuthApi, AuthUser } from '@corezilla/api';

@Injectable()
export class AuthEffects {
  /**
   * INJECTIONS
   * **/

  private readonly actions$ = inject(Actions);

  private readonly store = inject<Store<AppState>>(Store);

  private readonly authApi = inject(AuthApi);

  /**
   * Effect to log in user
   * **/
  login$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.login),
        tap(() => this.redirectToLogin())
      );
    },
    { dispatch: false }
  );

  /**
   * Effect to log-out user
   * **/
  logout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => this.redirectToLogout())
      );
    },
    { dispatch: false }
  );

  /**
   * Effect to handle password update of the current authenticated user
   * **/
  updatePassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.updateAuthUserPassword),
      exhaustMap(({ newPassword }) => {
        const authUser = this.store.selectSignal(
          AuthSelectors.selectUser
        )() as AuthUser;
        return this.authApi.updatePassword(authUser.id, { newPassword }).pipe(
          map(updated => AuthActions.updateAuthUserSuccess({ updated })),
          catchError(error => of(AuthActions.updateAuthUserError({ error })))
        );
      })
    );
  });

  /**
   * Effect to update an authenticated user
   * **/
  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.updateAuthUser),
      exhaustMap(({ toUpdate }) => {
        const authUser = this.store.selectSignal(
          AuthSelectors.selectUser
        )() as AuthUser;
        return this.authApi.updateUser({ ...authUser, ...toUpdate }).pipe(
          map(updated => AuthActions.updateAuthUserSuccess({ updated })),
          catchError(error => of(AuthActions.updateAuthUserError({ error })))
        );
      })
    );
  });

  /**
   * Helper method to check if a user is logged in
   * It tries to get the current user from the store, if not available, it tries to fetch it from the backend
   * **/
  isLoggedIn$(): Observable<boolean> {
    return this.getCurrentUser$().pipe(map(user => !!user));
  }

  private getCurrentUser$(): Observable<AuthUser | undefined> {
    const currentUser = this.store.selectSignal(AuthSelectors.selectUser)();

    if (currentUser) {
      return of(currentUser);
    }

    return this.authApi.getUser().pipe(
      tap(user => this.store.dispatch(AuthActions.setAuthUser({ user }))),
      catchError(error => {
        this.store.dispatch(AuthActions.setAuthError({ error }));
        return throwError(() => error);
      })
    );
  }

  /** Full-page redirect that starts the Keycloak OIDC login flow. */
  private redirectToLogin() {
    window.location.href = this.authApi.getLoginUrl();
  }

  /** Full-page redirect that ends the session via the ediconf/Keycloak logout chain. */
  private redirectToLogout() {
    window.location.href = this.authApi.getLogoutUrl();
  }
}
